/**
 * Reusable FAQ section component with structured data integration
 * Optimized for AEO (Answer Engine Optimization)
 * Uses semantic HTML for better SEO
 *
 * Renders one FAQPage JSON-LD node alongside the questions. Pages that show
 * several FAQ groups should instead render {@link FAQAccordion} per group plus
 * a single FAQStructuredData covering the combined list.
 */

'use client';

import React from 'react';
import { FAQStructuredData, type FAQItem } from '@/components/seo';
import { FAQAccordion } from './faq-accordion';

interface FAQSectionProps {
  faqs: FAQItem[];
  title?: string;
  description?: string;
  /**
   * Absolute URL of the page rendering this section.
   *
   * Required: it becomes the `url` of the emitted FAQPage JSON-LD. Omitting it
   * used to fall back to the site root, which made every page's FAQ schema
   * claim to live on the homepage.
   */
  url: string;
  defaultOpen?: boolean;
}

export function FAQSection({
  faqs,
  title = 'Frequently Asked Questions',
  description,
  url,
  defaultOpen = false,
}: FAQSectionProps) {
  return (
    <>
      <FAQStructuredData faqs={faqs} url={url} />
      <FAQAccordion faqs={faqs} title={title} description={description} defaultOpen={defaultOpen} />
    </>
  );
}
